import { KeyRound } from 'lucide-react';
import { Link } from 'react-router';

import { BuscarAgora } from '@/lib/captura/buscar-agora';
import { Dado, UltimaProxima } from '@/lib/captura/quando';
import {
  AcaoSugerida,
  AvisoOutroConsumidor,
  SituacaoCaptura,
} from '@/lib/captura/situacao-captura';
import { descreverNsu } from '@/lib/captura/textos';
import { cx } from '@/lib/cx';
import { DocumentoTexto } from '@/ui/components/documento-texto';
import { Button } from '@/ui/primitives/button';
import { type EstadoCaptura } from '@fiscoai/contracts';

/** O caminho para resolver o que impede a captura (a ação fica na empresa, não aqui). */
function Resolver({ estado }: { estado: EstadoCaptura }) {
  const id = estado.empresa.id;
  if (estado.situacao === 'DESLIGADA' || !estado.ativa)
    return (
      <Link to={`/empresas/${id}?aba=captura`} className="text-sm text-marca hover:underline">
        Ligue a captura na aba Captura SEFAZ da empresa
      </Link>
    );
  if (estado.situacao === 'SEM_CERTIFICADO' || estado.situacao === 'CERTIFICADO_VENCIDO')
    return (
      <Link to={`/empresas/${id}?aba=certificado`} className="text-sm text-marca hover:underline">
        Enviar o certificado A1 da empresa
      </Link>
    );
  return null;
}

function Nsu({ estado }: { estado: EstadoCaptura }) {
  const { nsu, fila } = descreverNsu(estado.ultNsu, estado.maxNsu);
  return (
    <Dado rotulo="NSU">
      {nsu} <span className="text-texto-suave">· {fila}</span>
    </Dado>
  );
}

function UsoPorChave({ estado }: { estado: EstadoCaptura }) {
  const perto = estado.consultasChaveNaHora >= estado.limiteConsultasChave - 2;
  return (
    <Dado rotulo="Por chave">
      <span className={cx(perto && 'text-alerta')}>
        {`${String(estado.consultasChaveNaHora)} de ${String(estado.limiteConsultasChave)} nesta hora`}
      </span>
    </Dado>
  );
}

interface LinhaEmpresaProps {
  estado: EstadoCaptura;
  selecionada: boolean;
  podeExecutar: boolean;
  aoBuscarChave: (estado: EstadoCaptura) => void;
}

/**
 * Uma empresa no painel: quem é e como está, quando buscou e vai buscar, onde está o NSU e quanto
 * da consulta por chave já foi usado; à direita, as ações. O nome abre o histórico abaixo.
 */
export function LinhaEmpresa({
  estado,
  selecionada,
  podeExecutar,
  aoBuscarChave,
}: LinhaEmpresaProps) {
  const { empresa } = estado;
  return (
    <li
      aria-current={selecionada || undefined}
      className={cx(
        'relative grid gap-x-6 gap-y-3 px-4 py-4 sm:px-5',
        'lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-center',
        selecionada &&
          'bg-superficie-selecao before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-marca',
      )}
    >
      <div className="grid min-w-0 gap-1.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <Link
            to={`?empresa=${empresa.id}`}
            className="truncate font-semibold text-texto hover:text-marca hover:underline"
            aria-label={`${empresa.nome}: ver histórico da captura`}
          >
            {empresa.nome}
          </Link>
          <DocumentoTexto valor={empresa.cnpj} className="text-xs text-texto-fraco" />
        </div>
        <SituacaoCaptura estado={estado} />
        <Resolver estado={estado} />
        <AcaoSugerida estado={estado} />
      </div>
      <dl className="grid content-center gap-1">
        <UltimaProxima estado={estado} />
      </dl>
      <dl className="grid content-center gap-1">
        <Nsu estado={estado} />
        <UsoPorChave estado={estado} />
      </dl>
      {podeExecutar && (
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <Button
            tamanho="sm"
            onClick={() => {
              aoBuscarChave(estado);
            }}
            aria-label={`Buscar pela chave uma nota de ${empresa.nome}`}
          >
            <KeyRound aria-hidden />
            Buscar pela chave
          </Button>
          <BuscarAgora estado={estado} tamanho="sm" />
        </div>
      )}
      {estado.suspeitaOutroConsumidor && <AvisoOutroConsumidor className="lg:col-span-4" />}
    </li>
  );
}
